import {
  fork,
  put,
  all,
  call,
  take,
  cancel,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import i18n from '../../i18n';
import toastr from 'toastr';
import { toDateFirebase } from '../../helpers/sharedFunction';
import types from './actionTypes';
import * as actions from './actions';

const db = getFirestore(rsf.app);

const aiAgentTransformer = (payload) => {
  const aiAgents = [];
  payload.forEach((doc) => {
    const data = doc.data();
    aiAgents.push({
      id: doc.id,
      ...data,
      createdAt: toDateFirebase(doc, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(doc, data, 'updatedAt').toDate(),
      }),
    });
  });
  return aiAgents;
};

function* syncAiAgentsSaga() {
  const companyId = yield select((state) => state.Dashboard.companyId);

  const aiAgentsRef = query(
    collection(db, 'aiAgents'),
    where('companyId', '==', companyId),
    orderBy('createdAt', 'desc'),
  );

  const task = yield fork(rsf.firestore.syncCollection, aiAgentsRef, {
    successActionCreator: actions.syncAiAgentsSuccess,
    failureActionCreator: actions.syncAiAgentsFailure,
    transform: (payload) => aiAgentTransformer(payload),
  });

  yield take(types.RESET_AI_AGENT_STATE);
  yield cancel(task);
}

export function* createAiAgentSaga({ aiAgent }) {
  try {
    const companyId = yield select((state) => state.Dashboard.companyId);

    yield call(rsf.firestore.addDocument, collection(db, 'aiAgents'), {
      ...aiAgent,
      companyId,
      active: aiAgent.active ?? true,
      createdAt: serverTimestamp(),
    });

    yield put(actions.createAiAgentSuccess());
    toastr.success(i18n.t('AI Agent created!'), '');
  } catch (error) {
    yield put(actions.createAiAgentFailure(error));
    toastr.error(error.message, 'Error');
  }
}

export function* updateAiAgentSaga({ aiAgent }) {
  try {
    // channel esce dal payload: e' immutabile dopo la creazione, e finora
    // lo imponeva solo la select disabilitata nel form. Qualunque altro
    // chiamante, o una regressione del form, poteva cambiare il canale di
    // un agente gia' in uso — e con esso costi, consenso e trasporto di
    // ogni acquisition order collegato.
    //
    // companyId leaves it for a harder reason: it is the tenant. Writing the
    // currently selected company back onto an existing agent would move that
    // agent into another tenant whenever the modal was opened before
    // switching company, because switching does not clear selectedAiAgent.
    // Ownership is decided once, on create.
    const { id, channel, companyId, ...aiAgentData } = aiAgent;
    if (!id) throw new Error('AI Agent not found');

    yield call(
      rsf.firestore.setDocument,
      doc(db, 'aiAgents', id),
      {
        ...aiAgentData,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );

    yield put(actions.updateAiAgentSuccess());
    toastr.success(i18n.t('AI Agent updated!'), '');
  } catch (error) {
    yield put(actions.updateAiAgentFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* aiAgentSaga() {
  yield all([
    takeLatest(types.SYNC_AI_AGENTS.REQUEST, syncAiAgentsSaga),
    takeLeading(types.CREATE_AI_AGENT.REQUEST, createAiAgentSaga),
    takeLeading(types.UPDATE_AI_AGENT.REQUEST, updateAiAgentSaga),
  ]);
}

export default aiAgentSaga;
